import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, TableRow } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import TableCell from 'src/components/Table/TableCell';
import { useTranslation } from 'react-i18next';
import TablePaginationCustom from 'src/components/Table/TablePaginationCustom';
import Table from 'src/components/Table';
import NotFoundRecords from 'src/components/NotFoundRecords';
import SantanderReportsRow from './SantanderReportsRow';

if (!process.env.REACT_APP_TABLE_SIZE) {
  console.error(
    'REACT_APP_TABLE_SIZE variable not loaded properly. Check your .env file please!'
  );
}

const Tbody = ({ reports }) =>
  reports.map(report => <SantanderReportsRow key={report.id} {...report} />);

const SantanderReportsTable = ({
  reports,
  loading,
  success,
  count,
  page,
  handleChangePage,
  ...rest
}) => {
  const { t } = useTranslation();

  const theads = useMemo(() => () => (
    <TableRow component="tr">
      <TableCell variant="head" align="center" key="date">
        {t('SANTANDER_REPORTS.DATE')}
      </TableCell>
      <TableCell variant="head" align="center" key="transactions">
        {t('SANTANDER_REPORTS.TRANSACTION_COUNT')}
      </TableCell>
      <TableCell variant="head" align="center" key="amount">
        {t('SANTANDER_REPORTS.DAILY_AMOUNT')}
      </TableCell>
      <TableCell variant="head" align="center" key="reconciliation">
        {t('SANTANDER_REPORTS.RECONCILIATION_STATUS')}
      </TableCell>
      <TableCell sx={{ width: '20%' }} variant="head" key="actions" />
    </TableRow>
  ));

  const tbody = useMemo(() => () => <Tbody reports={reports} />, [reports]);

  const pagination = useMemo(
    () => () => (
      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={count}
        rowsPerPage={+process.env.REACT_APP_TABLE_SIZE}
        page={page}
        onPageChange={handleChangePage}
        ActionsComponent={TablePaginationCustom}
        labelDisplayedRows={({ from, to, count: c }) => {
          return `${from}-${to} ${t('COMMON.OF')} ${c}`;
        }}
      />
    ),
    [page, count, t]
  );

  if (success && !loading && !reports.length) {
    return (
      <Card>
        <NotFoundRecords title="NOT_FOUND_RECORDS_MESSAGE.SANTANDER_REPORTS_TITLE" />
      </Card>
    );
  }

  return (
    <Table
      {...rest}
      tbody={tbody}
      theads={theads}
      loading={loading}
      pagination={pagination}
    />
  );
};

SantanderReportsTable.propTypes = {
  reports: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  success: PropTypes.bool.isRequired,
  count: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  handleChangePage: PropTypes.func.isRequired
};

export default SantanderReportsTable;
